import { action, observable, runInAction } from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import { HubConfigurationSettingEntity, IHubConfigurationSettingEntityDTO } from './common/hub-configuration-setting.entity';
import { HubConfigurationSettingsType, IEventHubsDAO, IHubConfigurationSettingsDTO } from '../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../api/implementations/event-hubs.dao';
import { handleApiException } from '../../../global/modal/alerts-host/alert-templates';
import { Mapper } from '../../../../infrastructure/mapper';

class HubConfigurationVM {
    @observable
    private entities: HubConfigurationSettingEntity[] = [];
    @observable
    private loading: boolean = false;
    private mapper: Mapper = new Mapper();

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        private readonly hubName: string,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly eventHubsDAO: IEventHubsDAO = IoC.Container.get(EventHubsDAO),
    ) {
        this.mapHubConfigSettingsToHubConfigEntitySetting();
        void this.loadConfigs();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public getEntityByType = (type: HubConfigurationSettingsType): HubConfigurationSettingEntity | undefined => {
        return this.entities.find(e => e.type === type);
    };

    @action
    private async loadConfigs(): Promise<void> {
        this.loading = true;
        try {
            const configs = await this.eventHubsDAO.getEventHubConfigs(this.accountId, this.eventHubId);
            this.initializeEntitiesFromConfigs(configs);
        } catch (error) {
            handleApiException(error);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @action
    private initializeEntitiesFromConfigs(configs: IHubConfigurationSettingsDTO): void {
        const mappedResponse = configs.map(
            setting => this.mapper.map(setting, 'IHubConfigurationSettingsDTO')
        );
        this.entities = mappedResponse.map(
            (settings: IHubConfigurationSettingEntityDTO) => new HubConfigurationSettingEntity(settings)
        );
    }

    private mapHubConfigSettingsToHubConfigEntitySetting = (): void => {
        this.mapper.addMapping(
            'IHubConfigurationSettingsDTO',
            {
                type: 'type',
                registrationEnabled: 'enabled',
            }
        );
    };
}

export { HubConfigurationVM };
